
import { MenuOutlined } from '@ant-design/icons';
import {
    Breadcrumb,
    Button,
    Col,
    Divider,
    Drawer,
    Flex,
    Grid,
    Layout, Menu, MenuProps, Row, Space, theme,
} from 'antd';
import {
    CSSProperties,
    useMemo, useState,
} from 'react';
import {
    Link, useLocation, useNavigate,
} from 'react-router-dom';

import {
    AppRoute, getHeaderNavItems,
} from '@/constants';
import { useUIStore, useUserStore } from '@/stores';

import { AppLogo } from './AppLogo';
import { HeaderMenu } from './HeaderMenu';
import { Logout } from './Logout';
import { ThemeSwitcher } from './ThemeSwitcher';
import { UserMenu } from './UserMenu';

const headerStyle: CSSProperties = {
    height: 'auto',
};

const leftColStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
};

const centerColStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
};

const rightColStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
};

const linkStyle: CSSProperties = {
    color: theme.getDesignToken().colorTextLightSolid,
};

export const pages = {
    statistics: {
        title: 'Статистика',
        path: AppRoute.Statistics,
    },
    projects: {
        title: 'Проекти',
        path: AppRoute.Projects,
    },
    users: {
        title: 'Користувачі',
        path: AppRoute.Users,
    },
    history: {
        title: 'Історія дій',
        path: AppRoute.Events,
    },
    objections: {
        title: 'Робота з запереченнями',
        path: AppRoute.Objections,
    },
    supportTickets: {
        title: 'Підтримка',
        path: AppRoute.SupportTickets,
    },
    settings: {
        title: 'Налаштування',
        path: AppRoute.Settings,
    },

};

export const AppHeader = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { user } = useUserStore();
    const screens = Grid.useBreakpoint();
    const isMobileNav = screens.lg === false;
    const [drawerOpen, setDrawerOpen] = useState(false);

    const navItems = useMemo(() => getHeaderNavItems(user?.role), [user?.role]);
    const { pageTitle } = useUIStore();

    const drawerMenuItems = useMemo(() => {
        if (!isMobileNav) {
            return navItems;
        }
        const list = [...(navItems ?? [])];
        const obIndex = list.findIndex(
            (item) =>
                item
                && typeof item === 'object'
                && 'key' in item
                && (item as { key: string }).key === AppRoute.Objections,
        );
        if (obIndex === -1) {
            return list;
        }
        const [obItem] = list.splice(obIndex, 1);
        list.push(obItem);
        return list;
    }, [navItems, isMobileNav]);

    const breadcrumbs = useMemo(() => {
        const crumbs = [{
            title: (
                <Link to={AppRoute.Projects} style={linkStyle}>
                    Головна
                </Link>
            ),
        }];

        const currentPage = Object.values(pages).find(page => pathname.startsWith(page.path));

        if (currentPage) {
            const isProjectSubPage = currentPage.path === AppRoute.Projects
                && pathname !== AppRoute.Projects
                && pathname !== AppRoute.ProjectCreate;

            const isProjectEdit = /^\/projects\/[^/]+\/edit$/.test(pathname);

            crumbs.push({
                title: isProjectSubPage ? (
                    <Link to={AppRoute.Projects} style={linkStyle}>
                        {currentPage.title}
                    </Link>
                ) : (
                    <span style={linkStyle}>{currentPage.title}</span>
                ),
            });

            if (isProjectSubPage && pageTitle) {
                const projectId = pathname.split('/')[2];
                crumbs.push({
                    title: isProjectEdit ? (
                        <Link to={`${AppRoute.Projects}/${projectId}`} style={linkStyle}>
                            {pageTitle}
                        </Link>
                    ) : (
                        <span style={linkStyle}>{pageTitle}</span>
                    ),
                });
            }
        }

        return crumbs;
    }, [pathname, pageTitle]);

    const handleDrawerMenuClick: MenuProps['onClick'] = (info) => {
        if (info.key !== 'external' && info.key !== 'SubMenu') {
            setDrawerOpen(false);
            navigate(info.key);
        }
    };

    return (
        <>
            <Layout.Header style={headerStyle}>
                {isMobileNav ? (
                    <Flex
                        align="center"
                        className="app-header-toolbar app-header-toolbar--mobile"
                        style={{ width: '100%' }}
                    >
                        <div style={{ width: 48, flexShrink: 0, display: 'flex', justifyContent: 'flex-start' }}>
                            <Button
                                type="text"
                                icon={<MenuOutlined style={{ color: linkStyle.color as string }} />}
                                onClick={() => setDrawerOpen(true)}
                                aria-label="Відкрити меню"
                            />
                        </div>
                        <Flex flex={1} justify="center" align="center" style={{ minWidth: 0 }}>
                            <Link to={AppRoute.Projects}>
                                <AppLogo size="large" isWhite />
                            </Link>
                        </Flex>
                        <div style={{ width: 48, flexShrink: 0 }} aria-hidden />
                    </Flex>
                ) : (
                    <Row gutter={10} className="app-header-toolbar">
                        <Col span={8} style={leftColStyle}>
                            <Breadcrumb items={breadcrumbs} />
                        </Col>
                        <Col span={8} style={centerColStyle}>
                            <Flex vertical justify="center" align="center" style={{
                                paddingTop: 8,
                                paddingBottom: 8,
                            }}>
                                <Link to={AppRoute.Projects}>
                                    <AppLogo size="large" isWhite />
                                </Link>
                            </Flex>
                        </Col>
                        <Col span={8} style={rightColStyle}>
                            <Space size="large">
                                <ThemeSwitcher />
                                <UserMenu />
                                <Logout />
                            </Space>
                        </Col>
                    </Row>
                )}
            </Layout.Header>
            <HeaderMenu />
            <Drawer
                title="Меню"
                placement="left"
                width={280}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' } }}
            >
                <Menu
                    mode="inline"
                    items={drawerMenuItems}
                    selectedKeys={[pathname]}
                    onClick={handleDrawerMenuClick}
                    style={{ flex: 1, border: 'none' }}
                />
                <Divider style={{ margin: 0 }} />
                <Flex vertical gap={16} style={{ padding: 16 }}>
                    <UserMenu forLightBackground />
                    <Flex align="center" justify="space-between" gap={12}>
                        <span>Тема оформлення</span>
                        <ThemeSwitcher />
                    </Flex>
                    <Logout useSolidHeaderStyle={false} />
                </Flex>
            </Drawer>
        </>
    );
};
